import path from 'path';

import Hls from 'hls.js';
import fetch from 'isomorphic-fetch';
import {
  getCredentialParamsString,
  PartCredentialType,
} from './part-credentials';

import { HEADER_ACCEPT } from 'constants/http';
import { M3U8, PLAIN_TEXT } from 'constants/mimes';

import { M3u8Adapter } from './m3u8adapter';

export const createHlsInstance = (
  playlistUrl: string,
  videoEl: HTMLVideoElement,
  hlsConfig: Hls.HlsConfig
): Hls => {
  const hls = new Hls(hlsConfig);

  hls.loadSource(playlistUrl);
  hls.attachMedia(videoEl);

  return hls;
};

const getCdnPrefix = (playlistUrl: string | URL): string => {
  const url = new URL(playlistUrl);

  return `${url.protocol}//${url.host}`;
};

export const getAbsolutePath = (playlistUrl: string): string => {
  const a = document.createElement('a');

  a.href = playlistUrl;

  const cdnPrefix = getCdnPrefix(playlistUrl);
  const parentPath = path.dirname(a.pathname);

  // Prepend each URL with the path on CloudFront. By default, these part
  // URLs are relative, which works when we're referencing the playlist
  // directly off of CloudFront, but we're actually referencing
  // blob://loom.com in this case.
  return cdnPrefix + parentPath;
};

/**
 * We take the URL of the m3u8 and do the following:
 * - convert every URL to an absolute URL
 * - append the CF signature to every single URL
 * We do this iteratively with every playlist referenced in the master m3u8,
 * so in the end all the URIs have the CF signatures appended
 *
 * The reason to do this and not have the player do it:
 * hls.js can do this, but hls.js does not support iOS, so we have do it anyway for
 * iOS (we couldn't find a better way)
 *
 *  @returns A single string with base64 encoded playlists
 */
export const getHlsPlaylistWithCredentials = async (
  playlistUrl: string,
  partCredentials: Partial<PartCredentialType>
): Promise<string> => {
  const credentialParamsString = getCredentialParamsString(partCredentials);

  const resp = await fetch(playlistUrl, {
    headers: {
      [HEADER_ACCEPT]: PLAIN_TEXT,
    },
  });
  const respText = await resp.text();
  const absolutePath = getAbsolutePath(playlistUrl);
  const finalPlaylist = M3u8Adapter.changeUrls(
    respText,
    absolutePath,
    credentialParamsString
  );
  const processor = async (url: string): Promise<string> => {
    try {
      return await getHlsPlaylistWithCredentials(url, partCredentials);
    } catch (err) {
      // do not error if some playlists are missing
      return '';
    }
  };
  const processedPlaylist = await M3u8Adapter.processPlaylists(
    finalPlaylist,
    processor
  );

  return `data:${M3U8};base64,${window.btoa(processedPlaylist)}`;
};
